/**
 * Entscheidungslogik für den Keycloak-Token-Refresh.
 *
 * Bewusst frei von next-auth-, React- und server-only-Importen: der
 * `jwt`-Callback läuft sowohl in der Node- als auch in der Edge-Runtime
 * (die Middleware liest die Session über `auth()`), und reine Funktionen
 * sind ohne next-auth-Bootstrap testbar. Gleiches Vorgehen wie in
 * `portal-access.ts`.
 *
 * WARUM das existiert: vorher hat jeder fehlgeschlagene Refresh — auch ein
 * einzelner Netzwerk-Schluckauf — `refreshToken` und `expiresAt` gelöscht.
 * Damit war die Refresh-Bedingung im `jwt`-Callback dauerhaft falsch, es gab
 * nie einen zweiten Versuch, und die Session blieb bis zum manuellen Logout
 * unbrauchbar (Sentry: „CombinedGraphQLErrors: Unauthorized"). Ein
 * vorübergehender Fehler darf die Session nicht endgültig verbrennen.
 */

/** Endgültig kaputt vs. später nochmal versuchen. */
export type RefreshFailureKind = 'transient' | 'permanent';

/** Signalwert in der Session, sobald der Refresh endgültig gescheitert ist. */
export const REFRESH_ACCESS_TOKEN_ERROR = 'RefreshAccessTokenError';

/**
 * Keycloak-`error`-Codes, bei denen ein weiterer Versuch sinnlos ist: das
 * Refresh-Token existiert nicht mehr (abgelaufen, rotiert, oder die
 * SSO-Session wurde beendet — etwa durch eine Abmeldung in einem anderen
 * Client des geteilten Realms `paeffgen`).
 */
const PERMANENT_ERROR_CODES = ['invalid_grant'];

/** Was der Token-Endpoint (oder das Netzwerk) geliefert hat. */
export interface RefreshFailureInput {
  /** HTTP-Status der Antwort; fehlt bei einem Netzwerk-/DNS-/Timeout-Fehler. */
  status?: number;
  /** `error`-Feld aus der OAuth-Fehlerantwort, z. B. `invalid_grant`. */
  errorCode?: string;
}

/**
 * Die für den Refresh relevanten Felder des next-auth-JWT. Absichtlich ein
 * eigener Typ statt `JWT`, damit diese Datei next-auth nicht importieren muss.
 */
export interface RefreshTokenState {
  accessToken?: string;
  refreshToken?: string;
  expiresAt?: number;
  error?: string;
  /** Zahl der aufeinanderfolgenden vorübergehenden Fehlschläge. */
  refreshFailures?: number;
  /** Unix-Sekunden: vor diesem Zeitpunkt wird nicht erneut versucht. */
  refreshRetryAt?: number;
}

/**
 * Nach so vielen aufeinanderfolgenden vorübergehenden Fehlschlägen gilt der
 * Refresh als endgültig gescheitert. Zusammen mit dem Backoff unten deckt das
 * rund elf Minuten Keycloak-Ausfall ab; danach ist eine Neuanmeldung
 * ehrlicher als weiter zu versuchen.
 */
export const MAX_TRANSIENT_REFRESH_ATTEMPTS = 5;

/** Wartezeit in Sekunden vor dem ersten Wiederholungsversuch. */
const RETRY_BASE_SECONDS = 5;

/** Obergrenze der Wartezeit, damit der Backoff nicht ins Unendliche wächst. */
const RETRY_MAX_SECONDS = 300;

/**
 * Exponentieller Backoff: 5 s, 20 s, 80 s, dann gedeckelt. Schützt Keycloak
 * davor, dass jeder Session-Zugriff (zwei Pods, viele Server-Anfragen pro
 * Seitenaufruf) einen eigenen Refresh-Versuch auslöst, solange der
 * Token-Endpoint gestört ist.
 */
function retryDelaySeconds(failures: number): number {
  const delay = RETRY_BASE_SECONDS * 4 ** (failures - 1);
  return Math.min(delay, RETRY_MAX_SECONDS);
}

/**
 * Entscheidet, ob ein Fehlschlag endgültig ist. Im Zweifel `transient`:
 * eine Session unnötig zu verbrennen ist der teurere Fehler — der Nutzer
 * müsste sich neu anmelden, obwohl nur kurz das Netz weg war.
 */
export function classifyRefreshFailure(
  input: RefreshFailureInput,
): RefreshFailureKind {
  if (input.errorCode && PERMANENT_ERROR_CODES.includes(input.errorCode)) {
    return 'permanent';
  }
  return 'transient';
}

/**
 * Berechnet die Refresh-Felder des Tokens nach einem Fehlschlag.
 *
 * `transient` lässt `refreshToken` und `expiresAt` stehen, damit der nächste
 * Session-Zugriff erneut refreshen kann. `permanent` räumt sie ab und setzt
 * `error`, damit oben davon erfahren wird, statt still ohne Bearer-Header
 * weiterzulaufen.
 */
export function applyRefreshFailure(
  token: RefreshTokenState,
  kind: RefreshFailureKind,
  nowSeconds: number,
): RefreshTokenState {
  const failures = (token.refreshFailures ?? 0) + 1;
  const givingUp = failures > MAX_TRANSIENT_REFRESH_ATTEMPTS;

  if (kind === 'transient' && !givingUp) {
    return {
      ...token,
      refreshFailures: failures,
      refreshRetryAt: nowSeconds + retryDelaySeconds(failures),
    };
  }

  // `refreshFailures` bleibt absichtlich stehen: in der Telemetrie ist
  // sichtbar, ob sofort aufgegeben wurde (invalid_grant) oder erst nach
  // mehreren vergeblichen Versuchen.
  return {
    ...token,
    accessToken: undefined,
    refreshToken: undefined,
    expiresAt: undefined,
    refreshRetryAt: undefined,
    refreshFailures: failures,
    error: REFRESH_ACCESS_TOKEN_ERROR,
  };
}

/**
 * Räumt nach einem geglückten Refresh die Fehlerspuren ab, damit ein
 * einzelner Ausrutscher später nicht auf den Backoff des nächsten
 * angerechnet wird.
 */
export function applyRefreshSuccess(
  token: RefreshTokenState,
): RefreshTokenState {
  return {
    ...token,
    refreshFailures: undefined,
    refreshRetryAt: undefined,
    error: undefined,
  };
}

/**
 * Ersetzt die frühere Bedingung `token.expiresAt && now > token.expiresAt`.
 * Zusätzlich zum Ablauf werden Wartefenster, fehlendes Refresh-Token und ein
 * bereits endgültiger Fehlschlag berücksichtigt.
 *
 * Bewusst OHNE Vorlauf-Puffer: der Refresh passiert weiterhin erst nach
 * Ablauf. Ein proaktiver Refresh vor Ablauf ist eine eigene Entscheidung
 * (siehe token-refresh.service.ts im Shop) und gehört nicht in diesen Fix.
 */
export function shouldAttemptRefresh(
  token: RefreshTokenState,
  nowSeconds: number,
): boolean {
  if (token.error) return false;
  if (!token.refreshToken) return false;
  if (token.expiresAt == null) return false;
  if (nowSeconds <= token.expiresAt) return false;
  if (token.refreshRetryAt != null && nowSeconds < token.refreshRetryAt) {
    return false;
  }
  return true;
}

/** Diagnosedaten eines Fehlschlags — Grundlage der Sentry-Meldung. */
export interface RefreshFailureReport {
  kind: RefreshFailureKind;
  /** HTTP-Status, fehlt bei Netzwerk-/DNS-/Timeout-Fehlern. */
  status?: number;
  /** OAuth-`error`, z. B. `invalid_grant`. */
  errorCode?: string;
  /** `error_description` von Keycloak, rein zur Diagnose. */
  errorDescription?: string;
  /** Fehlermeldung, falls schon der Aufruf selbst geworfen hat. */
  cause?: string;
  /** Der wievielte aufeinanderfolgende Fehlschlag das war. */
  attempt: number;
  /** Ob die Session damit endgültig aufgegeben wurde. */
  givingUp: boolean;
}

/** Alles, was der Refresh von außen braucht — injizierbar für Tests. */
export interface RefreshDeps {
  tokenUrl: string;
  clientId: string;
  clientSecret: string;
  fetchImpl: typeof fetch;
  nowSeconds: () => number;
  /** Telemetrie-Haken; hält diese Datei frei von Sentry-Importen. */
  onFailure?: (report: RefreshFailureReport) => void;
}

/** `json()` wirft bei einer HTML-Fehlerseite (Proxy, Ingress) — das darf den
 * Fehler nicht von „vorübergehend" zu „endgültig" verfälschen. */
async function readJsonSafely(response: Response): Promise<Record<string, unknown>> {
  try {
    const body = await response.json();
    return body && typeof body === 'object'
      ? (body as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}

/**
 * Führt den Refresh gegen Keycloak aus und liefert den neuen Token-Zustand.
 * Wirft nicht: jeder Fehlschlag kommt als Token-Zustand zurück, damit der
 * `jwt`-Callback immer ein verwendbares Ergebnis hat.
 */
export async function performTokenRefresh(
  token: RefreshTokenState,
  deps: RefreshDeps,
): Promise<RefreshTokenState> {
  const now = deps.nowSeconds();

  const fail = (
    kind: RefreshFailureKind,
    detail: Omit<RefreshFailureReport, 'kind' | 'attempt' | 'givingUp'>,
  ): RefreshTokenState => {
    const next = applyRefreshFailure(token, kind, now);
    deps.onFailure?.({
      ...detail,
      kind,
      attempt: next.refreshFailures ?? 1,
      givingUp: next.error != null,
    });
    return next;
  };

  let response: Response;
  try {
    response = await deps.fetchImpl(deps.tokenUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: deps.clientId,
        client_secret: deps.clientSecret,
        refresh_token: token.refreshToken ?? '',
      }),
    });
  } catch (error) {
    // Netzwerk, DNS, Timeout — nie die Schuld des Refresh-Tokens.
    return fail('transient', {
      cause: error instanceof Error ? error.message : String(error),
    });
  }

  if (!response.ok) {
    const body = await readJsonSafely(response);
    const errorCode =
      typeof body.error === 'string' ? body.error : undefined;
    const errorDescription =
      typeof body.error_description === 'string'
        ? body.error_description
        : undefined;
    return fail(classifyRefreshFailure({ status: response.status, errorCode }), {
      status: response.status,
      errorCode,
      errorDescription,
    });
  }

  const body = await readJsonSafely(response);
  const accessToken =
    typeof body.access_token === 'string' ? body.access_token : undefined;
  if (!accessToken) {
    // 200 ohne Token: kaputte Antwort, aber kein Beweis gegen das
    // Refresh-Token — also erneut versuchen.
    return fail('transient', {
      status: response.status,
      cause: 'Antwort ohne access_token',
    });
  }

  const expiresIn =
    typeof body.expires_in === 'number' ? body.expires_in : undefined;
  const rotated =
    typeof body.refresh_token === 'string' ? body.refresh_token : undefined;

  return applyRefreshSuccess({
    ...token,
    accessToken,
    refreshToken: rotated ?? token.refreshToken,
    expiresAt: expiresIn != null ? now + expiresIn : undefined,
  });
}
